import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import type { ParsedDSM, ParsedDSMTerrainBase } from './dsmTypes';
export type { ParsedDSM, ParsedDSMTerrainBase };

const DSM_CACHE_TIME = 30 * 60 * 1000;
const getDsmQueryKey = (dsmUrl?: string) => ['dsm', dsmUrl] as const;
const getTerrainBaseQueryKey = (dsmUrl?: string) => ['dsm-terrain-base', dsmUrl] as const;

const loadDsmParse = () => import('./dsmParse');

async function fetchTerrainBase(dsmUrl: string, queryClient: ReturnType<typeof useQueryClient>) {
	const { fetchAndParseDSM, buildTerrainBase } = await loadDsmParse();
	const parsed = await queryClient.fetchQuery(
		getDsmQueryKey(dsmUrl),
		() => fetchAndParseDSM(dsmUrl),
		{ staleTime: Infinity, cacheTime: DSM_CACHE_TIME },
	);
	return buildTerrainBase(parsed);
}

export const useDSMTerrainBase = (dsmUrl?: string) => {
	const queryClient = useQueryClient();
	return useQuery<ParsedDSMTerrainBase>({
		queryKey: getTerrainBaseQueryKey(dsmUrl),
		queryFn: async () => {
			if (!dsmUrl) throw new Error('No DSM URL');
			return fetchTerrainBase(dsmUrl, queryClient);
		},
		enabled: !!dsmUrl,
		staleTime: Infinity,
		cacheTime: DSM_CACHE_TIME,
	});
};

type IdleWindow = Window & {
	requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
	cancelIdleCallback?: (handle: number) => void;
};

export function useWarmDsm(dsmUrl?: string) {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!dsmUrl) return;

		const warm = () => {
			void queryClient.prefetchQuery(
				getTerrainBaseQueryKey(dsmUrl),
				() => fetchTerrainBase(dsmUrl, queryClient),
				{ staleTime: Infinity, cacheTime: DSM_CACHE_TIME },
			);
		};

		const idleWindow = window as IdleWindow;
		if (idleWindow.requestIdleCallback) {
			const id = idleWindow.requestIdleCallback(() => warm(), { timeout: 1500 });
			return () => idleWindow.cancelIdleCallback?.(id);
		}
		const id = setTimeout(warm, 300);
		return () => clearTimeout(id);
	}, [dsmUrl, queryClient]);
}
